import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { almacen, newOperationId, type Supply, type WasteReason } from '../api';
import {
  botellaDe,
  contadoEnBotellas,
  FRACCIONES,
  filaEnBlanco,
  filaLista,
  type FilaDeMerma,
} from '../cierreDeNoche';
import { Boton, Cabecera, Cargando, Fallo, Filtro, Pastilla, Tarjeta } from '../componentes';
import { cantidad, diaDeLima, hora, MOTIVO_DE_MERMA, motivosDeMerma, pideFirma } from '../formato';
import { tieneModulo } from '../negocio';
import { mayuscula, useNegocio, useVocabulario } from '../vocabulario';

/**
 * El cierre de la noche, desde el celular: lo que se tira y la barra.
 *
 * A las once, con la cocina apagada, quien cierra tiene el táper de arroz en
 * una mano y el celular en la otra. «Mermas y bajas» pide una por una, con su
 * lote y su motivo, en una pantalla pensada para la computadora del almacén:
 * nadie registraba las cuatro cosas que sobraron, y el costo real las cargaba
 * como «se fue sin receta». Aquí van varias de un golpe, con un solo motivo.
 *
 * La barra se cuenta como se mira una botella abierta: dos llenas y una a la
 * mitad. El sistema la guarda en su unidad —mililitros— y la diferencia queda
 * como un ajuste con su documento, igual que el conteo físico.
 */
export function CierreDeNoche() {
  const insumos = useQuery({ queryKey: ['insumos'], queryFn: almacen.supplies });

  return (
    <>
      <Cabecera
        titulo="Cierre de la noche"
        subtitulo="Lo que se tira y las botellas abiertas, desde el celular"
        ayuda="Varias mermas de un golpe con el mismo motivo, y el conteo de la barra por botellas: llenas más la que está abierta."
      />

      <div className="cuerpo cierre-de-noche">
        {insumos.isError ? <Fallo error={insumos.error} onReintentar={() => insumos.refetch()} /> : null}
        {insumos.isLoading ? (
          <Cargando que="los insumos" />
        ) : (
          <>
            <MermasDeLaNoche insumos={insumos.data ?? []} />
            <ConteoDeLaBarra insumos={insumos.data ?? []} />
          </>
        )}
      </div>
    </>
  );
}

function ordenados(insumos: readonly Supply[]): Supply[] {
  return [...insumos].sort((a, b) => a.name.localeCompare(b.name, 'es'));
}

function mensajeDeFallo(error: unknown): string {
  return error instanceof Error ? error.message : 'No se pudo registrar.';
}

function filaNueva(): FilaDeMerma {
  return { clave: newOperationId(), supplyId: '', cantidad: '' };
}

function MermasDeLaNoche({ insumos }: { insumos: readonly Supply[] }) {
  const vocabulario = useVocabulario();
  const negocio = useNegocio();
  const clientes = useQueryClient();

  const motivos = motivosDeMerma(tieneModulo(negocio, 'kitchen'));
  // Lo que más se tira al cerrar es lo que no aguanta al día siguiente.
  const [motivo, setMotivo] = useState<WasteReason>('natural_decay');
  const [pin, setPin] = useState('');
  // La clave de cada fila es su operationId: reintentar la misma no la registra dos veces.
  const [filas, setFilas] = useState<FilaDeMerma[]>(() => [filaNueva()]);
  const [fallos, setFallos] = useState<Record<string, string>>({});
  const [registradas, setRegistradas] = useState<number | null>(null);

  const listas = filas.filter(filaLista);
  const firma = pideFirma(motivo);

  const cambiar = (clave: string, cambio: Partial<FilaDeMerma>) => {
    setRegistradas(null);
    setFilas((previas) => {
      const nuevas = previas.map((fila) => (fila.clave === clave ? { ...fila, ...cambio } : fila));
      // Siempre una en blanco al final: la siguiente cosa que sobró.
      return nuevas.some(filaEnBlanco) ? nuevas : [...nuevas, filaNueva()];
    });
  };

  const registrar = useMutation({
    mutationFn: async () => {
      /*
       * Una por una y sin cortar en la primera que falle: si el pollo no pasa
       * porque no queda stock que dar de baja, el arroz y la salsa sí tienen
       * que quedar. Las que pasan salen de la lista; la que falló se queda con
       * su motivo para corregirla.
       */
      const hechas: string[] = [];
      const fallidas: Record<string, string> = {};

      for (const fila of listas) {
        try {
          let authorizationId: string | null = null;
          if (firma) {
            const autorizacion = await almacen.authorize({
              operationId: fila.clave,
              action: 'inventory.waste',
              pin: pin.trim(),
            });
            authorizationId = autorizacion.authorizationId;
          }
          await almacen.registerWaste({
            operationId: fila.clave,
            supplyId: fila.supplyId,
            quantity: fila.cantidad,
            reason: motivo,
            authorizationId,
          });
          hechas.push(fila.clave);
        } catch (error) {
          fallidas[fila.clave] = mensajeDeFallo(error);
        }
      }

      return { hechas, fallidas };
    },
    onSuccess: ({ hechas, fallidas }) => {
      setFallos(fallidas);
      setRegistradas(hechas.length);
      setFilas((previas) => {
        const quedan = previas.filter((fila) => !hechas.includes(fila.clave));
        return quedan.some(filaEnBlanco) ? quedan : [...quedan, filaNueva()];
      });
      if (hechas.length > 0) {
        setPin('');
        void clientes.invalidateQueries();
      }
    },
  });

  return (
    <Tarjeta>
      <h2 className="tarjeta__titulo">Lo que se tira</h2>

      <div className="filtros">
        {motivos.map((id) => (
          <Filtro key={id} texto={MOTIVO_DE_MERMA[id]} activo={motivo === id} onClick={() => setMotivo(id)} />
        ))}
      </div>

      {filas.map((fila) => {
        const elegido = insumos.find((insumo) => insumo.id === fila.supplyId);
        return (
          <div key={fila.clave} className="cierre-de-noche__fila">
            <select
              aria-label={mayuscula(vocabulario.insumo)}
              value={fila.supplyId}
              onChange={(e) => cambiar(fila.clave, { supplyId: e.target.value })}
            >
              <option value="">{mayuscula(vocabulario.insumo)}…</option>
              {ordenados(insumos).map((insumo) => (
                <option key={insumo.id} value={insumo.id}>
                  {insumo.name}
                </option>
              ))}
            </select>
            <input
              className="celda-input"
              inputMode="decimal"
              aria-label={`Cuánto se tira${elegido ? ` de ${elegido.name}` : ''}`}
              placeholder="0"
              value={fila.cantidad}
              onChange={(e) => cambiar(fila.clave, { cantidad: e.target.value })}
            />
            <span className="apagado mono">{elegido?.unit ?? ''}</span>
            {fallos[fila.clave] !== undefined ? (
              <p className="aviso-caja aviso-caja--peligro cierre-de-noche__ancho">{fallos[fila.clave]}</p>
            ) : null}
          </div>
        );
      })}

      {firma ? (
        <div className="campo">
          <label htmlFor="pin-cierre">PIN de quien responde</label>
          <p className="nota nota--pegada">
            «{MOTIVO_DE_MERMA[motivo]}» acusa a alguien: lo firma quien responde por el almacén, no quien
            lo encontró.
          </p>
          <input
            id="pin-cierre"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
          />
        </div>
      ) : null}

      {registradas !== null && registradas > 0 ? (
        <p className="aviso-caja">
          {registradas === 1 ? 'Quedó registrada 1 merma.' : `Quedaron registradas ${registradas} mermas.`}
        </p>
      ) : null}

      <div className="reposicion__pie">
        <span className="apagado">Una fila por cada cosa que sobró. Lote: el que vence antes.</span>
        <Boton
          variante="primario"
          onClick={() => registrar.mutate()}
          disabled={
            listas.length === 0 || registrar.isPending || (firma && !/^\d{4,8}$/.test(pin.trim()))
          }
        >
          {registrar.isPending
            ? 'Registrando…'
            : listas.length === 1
              ? 'Registrar 1 merma'
              : `Registrar ${listas.length} mermas`}
        </Boton>
      </div>
    </Tarjeta>
  );
}

interface FilaDeBarra {
  supplyId: string;
  enteras: string;
  fraccion: number;
}

function ConteoDeLaBarra({ insumos }: { insumos: readonly Supply[] }) {
  const vocabulario = useVocabulario();
  const clientes = useQueryClient();
  const [filas, setFilas] = useState<FilaDeBarra[]>([]);
  const [operationId, setOperationId] = useState(newOperationId);
  const [cerrado, setCerrado] = useState<{ document: string; adjustments: number } | null>(null);

  const porId = new Map(insumos.map((insumo) => [insumo.id, insumo]));
  const libres = ordenados(insumos).filter((insumo) => !filas.some((fila) => fila.supplyId === insumo.id));

  const lineas = filas.flatMap((fila) => {
    const insumo = porId.get(fila.supplyId);
    if (insumo === undefined) return [];
    const enteras = Number.parseInt(fila.enteras === '' ? '0' : fila.enteras, 10);
    if (!Number.isFinite(enteras)) return [];
    return [
      {
        insumo,
        counted: contadoEnBotellas(enteras, fila.fraccion, botellaDe(insumo).factor),
      },
    ];
  });

  const cambiar = (supplyId: string, cambio: Partial<FilaDeBarra>) =>
    setFilas((previas) => previas.map((fila) => (fila.supplyId === supplyId ? { ...fila, ...cambio } : fila)));

  const cerrar = useMutation({
    mutationFn: () => {
      const ahora = new Date().toISOString();
      const document = `BAR-${diaDeLima().replace(/-/g, '')}-${hora(ahora).replace(':', '')}`;
      return almacen
        .closeCount({
          operationId,
          document,
          lines: lineas.map((linea) => ({ supplyId: linea.insumo.id, counted: linea.counted })),
        })
        .then((resultado) => ({ document, adjustments: resultado.adjustments }));
    },
    onSuccess: (resultado) => {
      setCerrado(resultado);
      setFilas([]);
      setOperationId(newOperationId());
      void clientes.invalidateQueries();
    },
  });

  return (
    <Tarjeta>
      <h2 className="tarjeta__titulo">La barra</h2>
      <p className="nota">
        Cuenta cada botella: las llenas y cuánto queda en la abierta. Solo cambia lo que cuentes; lo demás
        queda como estaba.
      </p>

      {filas.map((fila) => {
        const insumo = porId.get(fila.supplyId);
        if (insumo === undefined) return null;
        const botella = botellaDe(insumo);
        const linea = lineas.find((l) => l.insumo.id === fila.supplyId);
        return (
          <div key={fila.supplyId} className="cierre-de-noche__fila">
            <strong className="cierre-de-noche__ancho">{insumo.name}</strong>
            <input
              className="celda-input"
              inputMode="numeric"
              aria-label={`Cuántas ${botella.nombre} llenas de ${insumo.name}`}
              placeholder="0"
              value={fila.enteras}
              onChange={(e) => cambiar(fila.supplyId, { enteras: e.target.value.replace(/\D/g, '') })}
            />
            <span className="apagado">{botella.nombre} llenas, y la abierta:</span>
            <div className="filtros cierre-de-noche__ancho">
              {FRACCIONES.map((fraccion) => (
                <Filtro
                  key={fraccion.valor}
                  texto={fraccion.texto}
                  activo={fila.fraccion === fraccion.valor}
                  onClick={() => cambiar(fila.supplyId, { fraccion: fraccion.valor })}
                />
              ))}
            </div>
            <span className="apagado mono cierre-de-noche__ancho">
              {/* Lo que se guarda, en la unidad del libro: que se vea antes de cerrar. */}
              Queda {linea === undefined ? '—' : `${cantidad(linea.counted)} ${insumo.unit}`} · el libro dice{' '}
              {cantidad(insumo.quantity)} {insumo.unit}
            </span>
            <button
              type="button"
              className="accion-fila"
              onClick={() => setFilas((previas) => previas.filter((f) => f.supplyId !== fila.supplyId))}
            >
              Quitar
            </button>
          </div>
        );
      })}

      <select
        aria-label={`Agregar un ${vocabulario.insumo} a contar`}
        value=""
        onChange={(e) => {
          const supplyId = e.target.value;
          if (supplyId === '') return;
          setCerrado(null);
          setFilas((previas) => [...previas, { supplyId, enteras: '', fraccion: 0 }]);
        }}
      >
        <option value="">Agregar una botella a contar…</option>
        {libres.map((insumo) => (
          <option key={insumo.id} value={insumo.id}>
            {insumo.name}
          </option>
        ))}
      </select>

      {cerrar.isError ? <Fallo error={cerrar.error} /> : null}
      {cerrado !== null ? (
        <p className="aviso-caja">
          <Pastilla texto={cerrado.document} tono="exito" /> dejó{' '}
          {cerrado.adjustments === 1 ? '1 ajuste' : `${cerrado.adjustments} ajustes`} en el kardex.
        </p>
      ) : null}

      <div className="reposicion__pie">
        <span className="apagado">La diferencia queda como ajuste, con su documento.</span>
        <Boton
          variante="primario"
          onClick={() => cerrar.mutate()}
          disabled={lineas.length === 0 || cerrar.isPending}
        >
          {cerrar.isPending ? 'Cerrando…' : 'Cerrar el conteo de la barra'}
        </Boton>
      </div>
    </Tarjeta>
  );
}
